import { useEffect } from 'react'
import { useUi } from '../../../app/ui'
import { allPlaces, selectPlace, useApp } from '../../store'
import { afterPaint } from '../../shell/afterPaint'

export function placeSwipeDirection(dx: number, dy: number, velocity = 0): number {
  if (Math.abs(dx) < Math.abs(dy) * 1.5 || Math.abs(dx) < 48 && !(Math.abs(dx) >= 16 && Math.abs(velocity) > 500)) return 0
  return dx < 0 ? 1 : -1
}

/** A horizontal touch release changes the whole mounted briefing, never a hero-only intermediate page. */
export function usePhonePlaceSwipe() {
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const phone = window.matchMedia('(max-width: 1099px)')
    let snap: typeof import('./snap').snapPlace | undefined, stop = () => {}, frame = 0
    const prepare = afterPaint(() => { void import('./snap').then(module => { snap = module.snapPlace }).catch(() => {}) })
    let start: { x: number; y: number; id: number; lastX: number; lastAt: number; velocity: number; dx: number; dy: number } | null = null
    let nodes: HTMLElement[] = [], offset = 0, origin = 0
    const reduced = () => useApp.getState().motion === 'reduced' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const paint = () => { frame = 0; for (const node of nodes) node.style.transform = `translate3d(${offset}px,0,0)` }
    const settle = (velocity = 0) => {
      cancelAnimationFrame(frame); frame = 0
      if (snap && offset && !reduced()) stop = snap(nodes, offset, velocity * .35, x => { offset = x })
      else { offset = 0; for (const node of nodes) { node.style.transform = ''; node.style.willChange = '' } }
    }
    let consumed = false
    const down = (event: PointerEvent) => {
      consumed = false
      start = null
      const target = event.target instanceof Element ? event.target : null
      if (!phone.matches || event.pointerType !== 'touch' || !event.isPrimary || useUi.getState().phoneList || useUi.getState().sheet || document.querySelector('dialog[open]')) { start = null; return }
      if (!target?.closest('[data-zone="hero"],[data-zone="story"]') || target.closest('input,select,textarea,[role="slider"]')) return
      if (allPlaces(useApp.getState()).length < 2) return
      stop(); cancelAnimationFrame(frame); frame = 0; origin = offset
      nodes = [...document.querySelectorAll<HTMLElement>('[data-zone="hero"],[data-zone="story"]')]
      for (const node of nodes) node.style.willChange = reduced() ? '' : 'transform'
      start = { x: event.clientX, y: event.clientY, id: event.pointerId, lastX: event.clientX, lastAt: event.timeStamp, velocity: 0, dx: 0, dy: 0 }
    }
    const move = (event: PointerEvent) => {
      if (!start || start.id !== event.pointerId) return
      start.dx = event.clientX - start.x; start.dy = event.clientY - start.y
      const elapsed = event.timeStamp - start.lastAt
      if (elapsed > 0) start.velocity = (event.clientX - start.lastX) / elapsed * 1000
      start.lastX = event.clientX; start.lastAt = event.timeStamp
      if (Math.abs(start.dy) > 12 && Math.abs(start.dy) > Math.abs(start.dx)) { start = null; settle(); return }
      if (Math.abs(start.dx) < 8 || reduced()) return
      offset = origin + Math.sign(start.dx) * 96 * (1 - Math.exp(-Math.abs(start.dx) / 220))
      if (!frame) frame = requestAnimationFrame(paint)
    }
    const up = (event: PointerEvent) => {
      if (!start || start.id !== event.pointerId) return
      const dx = event.clientX - start.x, dy = event.clientY - start.y
      const velocity = event.timeStamp - start.lastAt > 100 ? 0 : start.velocity
      start = null
      const direction = placeSwipeDirection(dx, dy, velocity)
      settle(velocity)
      if (!direction) return
      const state = useApp.getState(), places = allPlaces(state)
      if (places.length < 2) return
      const at = Math.max(0, places.findIndex((place) => place.id === state.selectedId))
      consumed = true
      selectPlace(places[(at + direction + places.length) % places.length].id)
    }
    const cancel = () => { start = null; settle() }
    const click = (event: MouseEvent) => { if (consumed && event.detail > 0) { consumed = false; event.preventDefault(); event.stopImmediatePropagation() } }
    window.addEventListener('pointerdown', down, { passive: true })
    window.addEventListener('pointermove', move, { passive: true })
    window.addEventListener('pointerup', up, { passive: true })
    window.addEventListener('pointercancel', cancel, { passive: true })
    window.addEventListener('click', click, true)
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const reset = () => { start = null; stop(); cancelAnimationFrame(frame); frame = 0; offset = 0; for (const node of nodes) { node.style.transform = ''; node.style.willChange = '' } }
    media.addEventListener('change', reset)
    const unsubscribe = useApp.subscribe((state, previous) => { if (state.motion !== previous.motion || state.selectedId !== previous.selectedId && start) reset() })
    return () => { prepare(); reset(); unsubscribe(); media.removeEventListener('change', reset); window.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', cancel); window.removeEventListener('click', click, true) }
  }, [])
}
